import React, { useState, useEffect, useRef } from 'react';
import { cn, getRatioClassnames } from '@/lib/utils/cn';
import Image from 'next/image';

type FeatureItem = {
  title: string;
  description: string;
  image: string;
};

type FeatureScrollSectionProps = {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  items: FeatureItem[];
  className?: string;
  ratio?: string;
};

const FeatureScrollSection: React.FC<FeatureScrollSectionProps> = ({ title, subtitle, items, className = '', ratio = '1:1' }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [left, right] = getRatioClassnames(ratio);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const { top, height } = sectionRef.current.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, -top / (height - window.innerHeight)));

      const newIndex = Math.min(items.length - 1, Math.floor(scrollProgress * items.length));
      setActiveIndex(newIndex);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [items.length]);

  return (
    <section ref={sectionRef} className={cn('relative bg-white', className)} style={{ minHeight: `${items.length * 100}vh` }}>
      <div className="sticky top-0 h-screen flex items-center overflow-y-auto">
        <div className="mx-auto max-w-screen-xl w-full">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 xl:gap-12 items-center px-4 py-4 sm:py-6 md:px-2 lg:px-2 lg:py-12 xl:px-0">
            {/* Left content */}
            <div className={cn('text-left', left)}>
              <h2 className="mb-2 sm:mb-4 text-2xl sm:text-3xl font-extrabold leading-tight sm:leading-none tracking-tight text-gray-900 md:text-4xl">{title}</h2>
              <p className="mb-4 sm:mb-8 max-w-2xl text-sm sm:text-base">{subtitle}</p>

              {/* Feature items list */}
              <div className="space-y-2 sm:space-y-3 md:space-y-6">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      'p-2 sm:p-3 md:p-6 rounded-lg transition-all duration-500 border',
                      activeIndex === index ? 'bg-white border-primary shadow-lg' : 'bg-gray-50 border-gray-200 opacity-50'
                    )}
                  >
                    <h3 className={cn('text-base sm:text-lg md:text-xl font-bold mb-1 sm:mb-2 transition-colors', activeIndex === index ? 'text-gray-900' : 'text-gray-500')}>
                      {item.title}
                    </h3>
                    <p className={cn('text-sm sm:text-base transition-colors', activeIndex === index ? 'text-gray-600' : 'text-gray-400')}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className={cn('relative p-2 sm:p-5', right)}>
              <div className="relative overflow-hidden rounded-2xl aspect-[5/4]">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      'absolute inset-0 transition-all duration-700',
                      activeIndex === index ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                    )}
                    style={{
                      transform: activeIndex === index ? 'translateY(0)' : 'translateY(20px)',
                    }}
                  >
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                      className="object-cover rounded-2xl"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      priority={index === 0}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureScrollSection;
